import React from 'react';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import { useHistory, useLocation } from 'react-router-dom';
import { Dashboard } from '../../../Services/DashboardService';
import { Paths } from '../../../Routes/routePaths';
import { useInsightsStyles } from './styles';
import {
  VALID_PERIODS, customerIdFromApi, customerInsightsRoute, requestErrorMessage, routeForApi, scrollToAnchor,
} from './utils';
import { anchorForAttention, routeForRecord, routeForTarget } from './attentionTargets';
import { ExportContext, useExcelDownloads } from './exportContext';
import { Panel, ErrorPanel } from './components/shared';
import InsightsHeader from './components/InsightsHeader';
import AiSummary from './components/AiSummary';
import KpiGrid from './components/KpiGrid';
import AttentionList from './components/AttentionList';
import NewThisWeek from './components/NewThisWeek';
import LearnersWhoAndWhere from './components/LearnersWhoAndWhere';
import LearningHubInsights from './components/LearningHubInsights';
import DailyLogins from './components/DailyLogins';
import SyncHealth from './components/SyncHealth';
import LicenceJourney from './components/LicenceJourney';
import EngagementAndGrowth from './components/EngagementAndGrowth';
import CustomerInsights from './components/CustomerInsights';
import Impersonation from './components/Impersonation';
import ProjectsAndCourses from './components/ProjectsAndCourses';
import HowWeCount from './components/HowWeCount';
import AttentionDrawer from './components/AttentionDrawer';
import InsightsSkeleton from './components/InsightsSkeleton';

const dashboardService = new Dashboard();

const FATAL_TITLES = {
  401: 'Please sign in again',
  403: 'Only superadmins can view this page',
  400: 'This request was not accepted',
  404: 'Not found',
};

const getPeriodFromSearch = search => {
  const requested = new URLSearchParams(search).get('period');
  return VALID_PERIODS.includes(requested) ? requested : '30d';
};

const SuperadminInsights = () => {
  const classes = useInsightsStyles();
  const history = useHistory();
  const location = useLocation();
  const [period, setPeriod] = React.useState(() => getPeriodFromSearch(location.search));
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [fatal, setFatal] = React.useState(null);
  const [attentionItem, setAttentionItem] = React.useState(null);
  const requestId = React.useRef(0);
  const { busy, download } = useExcelDownloads();

  const setUrlPeriod = React.useCallback((next, replace = false) => {
    const params = new URLSearchParams(location.search);
    params.set('period', next);
    history[replace ? 'replace' : 'push']({ ...location, search: `?${params.toString()}` });
  }, [history, location]);

  React.useEffect(() => {
    const requested = new URLSearchParams(location.search).get('period');
    if (!VALID_PERIODS.includes(requested)) setUrlPeriod('30d', true);
    // URL normalization is intentionally limited to page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One request per user action: no automatic retries, because an uncached build takes up to 70 seconds.
  const load = React.useCallback(async (nextPeriod, refresh = false) => {
    const currentRequest = ++requestId.current;
    if (data) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const response = await dashboardService.getSuperadminInsights(nextPeriod, refresh);
      if (currentRequest !== requestId.current) return;
      if (!response?.status || !response.data) throw new Error(response?.message || 'Something went wrong');
      setFatal(null);
      setData(response.data);
    } catch (requestError) {
      if (currentRequest !== requestId.current) return;
      const status = requestError?.response?.status;
      const message = requestErrorMessage(requestError);
      if (FATAL_TITLES[status]) {
        setFatal({ status, message });
        setData(null);
        return;
      }
      setError(message);
    } finally {
      if (currentRequest === requestId.current) { setLoading(false); setRefreshing(false); }
    }
  }, [data]);

  React.useEffect(() => {
    load(period);
    return () => { requestId.current += 1; };
    // A period change is the only automatic refetch; callback identity must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const exporter = React.useMemo(() => ({
    busy,
    exportSection: section => download(
      section,
      () => dashboardService.exportSuperadminInsights(period, section),
      `superadmin-insights-${section}-${period}.xlsx`,
    ),
  }), [busy, download, period]);

  const changePeriod = next => {
    if (next === period) return;
    setPeriod(next);
    setUrlPeriod(next);
  };

  const navigateDrilldown = drilldown => {
    if (!drilldown) return;
    if (drilldown.anchor) {
      scrollToAnchor(drilldown.anchor);
      return;
    }
    history.push(routeForApi(drilldown.api));
  };

  const openCustomer = row => {
    const explicit = routeForTarget(row?.drilldown?.target || row?.links?.customer?.target);
    const route = explicit || customerInsightsRoute(customerIdFromApi(row?.drilldown?.api) || row?.customer_id);
    if (route) history.push(route);
  };

  const openProject = row => {
    const route = routeForTarget(row?.drilldown?.target || row?.links?.project?.target || {
      page: 'project', project_id: row?.project_id,
    });
    history.push(route || Paths.SuperadminProjects);
  };

  const openAttention = item => {
    if (scrollToAnchor(anchorForAttention(item))) return;
    if (Array.isArray(item?.records) && item.records.length === 1) {
      const route = routeForRecord(item, item.records[0]);
      if (route) { history.push(route); return; }
    }
    setAttentionItem(item);
  };

  const navigateFromAttention = route => {
    setAttentionItem(null);
    if (route) history.push(route);
  };

  if (fatal) {
    return (
      <main className={classes.page}>
        <ErrorPanel
          title={FATAL_TITLES[fatal.status]}
          message={fatal.message}
          action={fatal.status === 401
            ? <Button variant="contained" color="primary" onClick={() => history.replace(Paths.Login)}>Sign in</Button>
            : fatal.status === 403 ? null : <Button variant="contained" color="primary" onClick={() => load(period)}>Try again</Button>}
        />
      </main>
    );
  }

  if (loading && !data) return <main className={classes.page}><InsightsSkeleton /></main>;

  if (!data) {
    return (
      <main className={classes.page}>
        <ErrorPanel
          title="We couldn't load platform insights"
          message={error}
          action={<Button variant="contained" color="primary" onClick={() => load(period)}>Try again</Button>}
        />
      </main>
    );
  }

  return (
    <ExportContext.Provider value={exporter}>
      <main className={classes.page}>
        {refreshing ? <LinearProgress className={classes.progress} aria-label="Updating platform insights" /> : null}
        {error ? <Panel role="alert" style={{ marginBottom: 16, borderColor: '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}><div><Typography style={{ fontWeight: 700 }}>We couldn&apos;t update platform insights</Typography><Typography color="textSecondary">{error} Showing the last successful update.</Typography></div><Button variant="outlined" color="primary" onClick={() => load(period)}>Try again</Button></Panel> : null}
        <InsightsHeader meta={data.meta || {}} period={period} onPeriodChange={changePeriod} onRefresh={() => load(period, true)} refreshing={refreshing} scopeInTooltip />
        <div className={refreshing ? classes.loadingOverlay : undefined}>
          <AiSummary items={data.summary || []} />
          <KpiGrid items={data.kpis || []} onDrilldown={navigateDrilldown} />
          <AttentionList items={data.attention || []} onAction={openAttention} exportKey="attention" />
          <NewThisWeek data={data.new_this_week || {}} />
          <LearnersWhoAndWhere data={data.users || {}} onCustomer={openCustomer} onProject={openProject} />
          <LearningHubInsights data={data.learning_hub || {}} onProject={openProject} />
          <DailyLogins data={data.logins || {}} service={dashboardService} />
          <SyncHealth data={data.sync_health || {}} />
          <LicenceJourney data={data.licences || {}} />
          <EngagementAndGrowth learners={data.learners || {}} trends={data.trends || {}} />
          <CustomerInsights data={data.customers || {}} onCustomer={openCustomer} />
          <Impersonation data={data.impersonation || {}} />
          <ProjectsAndCourses projects={data.projects || {}} courses={data.courses || {}} />
          <HowWeCount glossary={data.glossary || []} legacyFields={data.legacy_fields || []} />
        </div>
        <AttentionDrawer item={attentionItem} data={data} service={dashboardService} onClose={() => setAttentionItem(null)} onNavigate={navigateFromAttention} />
      </main>
    </ExportContext.Provider>
  );
};

export default SuperadminInsights;
